/**
 * Reusable DataTable: sticky header, vertical + horizontal scroll, configurable columns, sortable headers.
 * Sorting is handled by the API: this component only manages sort state and calls onSort so the parent
 * can update state and re-call the API with sortColumn/sortDirection.
 *
 * @example (API-driven sorting)
 * const [sortColumn, setSortColumn] = useState<string | null>('name');
 * const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
 * const [rows, setRows] = useState<Row[]>([]);
 *
 * const fetchData = useCallback(async () => {
 *   const res = await api.getReport({ sortColumn, sortDirection, ... });
 *   setRows(res.data);
 * }, [sortColumn, sortDirection]);
 * useEffect(() => { fetchData(); }, [fetchData]);
 *
 * const handleSort = (key: string) => {
 *   setSortDirection((d) => (sortColumn === key && d === 'asc' ? 'desc' : 'asc'));
 *   setSortColumn(key);
 *   // fetchData() will run from useEffect when state updates
 * };
 *
 * <DataTable<Row> data={rows} columns={columns} sortColumn={sortColumn} sortDirection={sortDirection}
 *   onSort={handleSort} maxHeight="400px" emptyMessage="No rows" />
 */
import type { DataTableProps, ColumnConfig } from './types';
import './DataTable.css';

function DataTable<T extends object>({
  data,
  columns,
  sortColumn,
  sortDirection,
  onSort,
  emptyMessage = 'No data to display',
  maxHeight = '400px',
  className = '',
  getRowKey = (_, index) => index,
  onRowClick,
}: DataTableProps<T>) {
  const handleHeaderClick = (col: ColumnConfig<T>) => {
    if (col.sortable !== false) {
      onSort(col.sortKey ?? col.key);
    }
  };

  const getCellValue = (row: T, key: string): unknown => {
    const value = (row as Record<string, unknown>)[key];
    return value ?? '';
  };

  return (
    <div
      className={`data-table-wrapper ${className}`}
      style={{ maxHeight, height: maxHeight, minHeight: 0 }}
      role="region"
      aria-label="Data table"
    >
      <div className="data-table-scroll">
        <table className="data-table">
          <thead className="data-table-head">
            <tr>
              {columns.map((col) => {
                const isSorted = sortColumn === (col.sortKey ?? col.key);
                const sortable = col.sortable !== false;
                return (
                  <th
                    key={col.key}
                    className={`data-table-th ${sortable ? 'data-table-th--sortable' : ''} data-table-th--${col.align ?? 'left'}`}
                    style={
                      col.minWidth != null
                        ? { minWidth: typeof col.minWidth === 'number' ? `${col.minWidth}px` : col.minWidth }
                        : undefined
                    }
                    onClick={() => handleHeaderClick(col)}
                    scope="col"
                  >
                    <span className="data-table-th-content">
                      {col.label}
                      {sortable && (
                        <span className="data-table-sort-icon" aria-hidden>
                          {isSorted ? (
                            sortDirection === 'asc' ? (
                              <span className="data-table-sort-arrow" title="Sorted ascending">↑</span>
                            ) : (
                              <span className="data-table-sort-arrow" title="Sorted descending">↓</span>
                            )
                          ) : (
                            <span className="data-table-sort-arrow data-table-sort-arrow--muted">↕</span>
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="data-table-body">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="data-table-empty">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={getRowKey(row, index)}
                  className={`data-table-row${onRowClick ? ' data-table-row--clickable' : ''}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  role={onRowClick ? 'button' : undefined}
                >
                  {columns.map((col) => {
                    const value = getCellValue(row, col.key);
                    const content = col.render ? col.render(value, row) : String(value);
                    return (
                      <td
                        key={col.key}
                        className={`data-table-td data-table-td--${col.align ?? 'left'}`}
                        style={
                          col.minWidth != null
                            ? { minWidth: typeof col.minWidth === 'number' ? `${col.minWidth}px` : col.minWidth }
                            : undefined
                        }
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;
